/* eslint-disable boundaries/element-types */
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  message,
  Select,
  Space,
  Typography,
} from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { RightSideDrawerProps } from 'components/rightDrawer/utils/interfaces'
import { useRightDrawer } from 'components/rightDrawer/utils/RightDrawerProvider'
import { useGeckoAuth } from 'features/auth'
import {
  RautGDriveRolodexSyncData,
  triggerGDriveRolodexSync,
  useRolodexEdit,
} from 'features/fde'
import { useRolodexEntries } from 'features/rolodex'
import React, { useEffect, useState } from 'react'
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'
import { z } from 'zod'

import { AsSyncStatusSection } from './asSyncStatus/AsSyncStatusSection'

const { Title, Text } = Typography

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledNote = styled(Text)`
  font-size: 12px;
  text-align: center;
  margin-top: 16px;
`

export const RautInspectionSyncProps: RightSideDrawerProps = {
  title: 'RAUT Inspection Sync',
}

const InspectionSyncSchema = z.object({
  gdriveFolderIDs: z
    .array(z.string())
    .min(1, 'At least one Google Drive Folder ID is required'),
  enableAutoMeasurement: z.boolean().optional(),
  reprocessAScan: z.boolean().optional(),
})

type InspectionSyncSchema = z.infer<typeof InspectionSyncSchema>

export const RAUTInspectionSync = () => {
  const { drawerEntry } = useRightDrawer()
  const [messageApi, contextHolder] = message.useMessage()
  const [isSyncing, setIsSyncing] = useState(false)
  const { authorizedAxios: axios } = useGeckoAuth()
  const { RAUT_GDRIVE_ROLODEX_SYNC_URL } = useRuntimeVariables()
  const { rolodexPatch, rolodexPut } = useRolodexEdit()

  const inspectionSlug = drawerEntry?.raw.id

  const { entries, isLoading } = useRolodexEntries(
    inspectionSlug
      ? { ids: [inspectionSlug, `${inspectionSlug}_nde`] }
      : undefined
  )
  const [isInitializing, setIsInitializing] = useState(true)
  const inspectionEntry = entries?.find((entry) => entry.id === inspectionSlug)
  const ndeEntry = entries?.find(
    (entry) => entry.id === `${inspectionSlug}_nde`
  )

  useEffect(() => {
    const initializeNDEEntry = async () => {
      if (inspectionSlug && !ndeEntry) {
        try {
          await rolodexPut([
            {
              id: `${inspectionSlug}_nde`,
              type: RolodexEntryType.NDE,
              data: {},
              tags: { inspection: inspectionSlug },
            },
          ])
        } catch {
          messageApi.error('Failed to create NDE entry. Please try again.')
          return
        }
      }
      setIsInitializing(false)
    }

    if (!isLoading) {
      initializeNDEEntry()
    }
  }, [inspectionSlug, ndeEntry, isLoading, rolodexPut, messageApi])

  const [defaultFolderIDs, setDefaultFolderIDs] = useState<string[]>([])

  const form = useForm<InspectionSyncSchema>({
    mode: 'onBlur',
    resolver: zodResolver(InspectionSyncSchema),
    defaultValues: {
      gdriveFolderIDs: [],
      enableAutoMeasurement: false,
      reprocessAScan: false,
    },
  })

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = form

  useEffect(() => {
    if (
      inspectionEntry?.data?.gdrive_folder_ids &&
      Array.isArray(inspectionEntry.data.gdrive_folder_ids)
    ) {
      setDefaultFolderIDs(inspectionEntry.data.gdrive_folder_ids)
      form.reset({
        gdriveFolderIDs: inspectionEntry.data.gdrive_folder_ids,
      })
    }
  }, [inspectionEntry, form])

  const watchedFolderIDs = watch('gdriveFolderIDs')
  const watchedEnableAutoMeasurement = watch('enableAutoMeasurement')
  const watchedReprocessAScan = watch('reprocessAScan')

  type SyncResult = {
    folderID: string
    success: boolean
    error?: unknown
  }

  const onSubmit: SubmitHandler<InspectionSyncSchema> = async (data) => {
    if (!inspectionSlug || !ndeEntry) {
      messageApi.error('Inspection or NDE entry not found. Please try again.')
      return
    }

    setIsSyncing(true)

    // Update inspection entry if folder IDs have changed
    if (JSON.stringify(watchedFolderIDs) !== JSON.stringify(defaultFolderIDs)) {
      try {
        await rolodexPatch([
          {
            id: inspectionSlug,
            data: {
              gdrive_folder_ids: watchedFolderIDs,
            },
          },
        ])
      } catch {
        messageApi.error(
          'Failed to update inspection entry with new folder IDs.'
        )
        setIsSyncing(false)
        return
      }
    }

    // Trigger sync for each folder ID
    const syncPromises: Promise<SyncResult>[] = data.gdriveFolderIDs.map(
      async (folderID) => {
        return new Promise((resolve) => {
          let isResolved = false

          const timeoutId = setTimeout(() => {
            if (!isResolved) {
              isResolved = true
              resolve({ folderID, success: true })
            }
          }, 5000)

          const syncData: RautGDriveRolodexSyncData = {
            folder_id: folderID,
            inspection_slug: inspectionSlug,
          }

          if (watchedEnableAutoMeasurement !== undefined) {
            syncData.enable_auto_measure = watchedEnableAutoMeasurement
          }
          if (watchedReprocessAScan !== undefined) {
            syncData.reprocess_ascan = watchedReprocessAScan
          }

          triggerGDriveRolodexSync(
            RAUT_GDRIVE_ROLODEX_SYNC_URL,
            axios,
            syncData
          ).then(
            () => {
              if (!isResolved) {
                clearTimeout(timeoutId)
                isResolved = true
                resolve({ folderID, success: true })
              }
            },
            (error) => {
              if (!isResolved) {
                clearTimeout(timeoutId)
                isResolved = true
                resolve({ folderID, success: false, error })
              }
            }
          )
        })
      }
    )

    const results = await Promise.all(syncPromises)

    const failedSyncs = results.filter((result) => !result.success)

    if (failedSyncs.length > 0) {
      messageApi.error(
        `Failed to sync ${failedSyncs.length} folder(s). Please try again.`
      )
    } else {
      messageApi.success(
        'Sync process has been initiated successfully. For larger inspections, this process may take a couple of hours to complete.',
        5
      )
    }

    setIsSyncing(false)
  }

  if (isLoading || isInitializing) {
    return <Space>Loading inspection data...</Space>
  }

  if (!inspectionSlug || !ndeEntry) {
    return (
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <Title level={3}>Advanced Services Inspection Sync</Title>
        <Text type='warning'>
          Inspection or NDE entry not found. Please try again.
        </Text>
      </Space>
    )
  }

  return (
    <FormProvider {...form}>
      {contextHolder}
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <Title level={3}>Advanced Services Inspection Sync</Title>
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <Form.Item label='Inspection Slug'>
            <Input value={inspectionSlug} disabled />
          </Form.Item>

          <Controller
            name='gdriveFolderIDs'
            control={control}
            render={({ field }) => (
              <Form.Item
                label='Google Drive Folder IDs'
                validateStatus={errors.gdriveFolderIDs ? 'error' : ''}
                help={errors.gdriveFolderIDs?.message}>
                <Select
                  {...field}
                  key={defaultFolderIDs.join(',')}
                  mode='tags'
                  style={{ width: '100%' }}
                  placeholder='Enter Google Drive Folder IDs'
                  onChange={(value) => field.onChange(value)}
                />
              </Form.Item>
            )}
          />

          <Title level={4}>Auto-Measurement</Title>
          <Form.Item>
            <>
              <Controller
                name='enableAutoMeasurement'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}>
                    Enable Auto-Measurement
                  </Checkbox>
                )}
              />
              <Controller
                name='reprocessAScan'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    disabled={!watchedEnableAutoMeasurement}
                    onChange={(e) => field.onChange(e.target.checked)}>
                    Reprocess A-Scan
                  </Checkbox>
                )}
              />
            </>
          </Form.Item>

          <Form.Item>
            <Button
              htmlType='submit'
              loading={isSyncing}
              disabled={isSyncing}
              block>
              {isSyncing ? 'Syncing...' : 'Sync Data'}
            </Button>
          </Form.Item>
        </StyledForm>

        <StyledNote>
          Note: The sync process may take some time to complete, especially for
          larger inspections. You can close this drawer and continue working
          while the sync runs in the background.
        </StyledNote>
        <div>
          <Divider />
          <Title level={4}>Sync Status</Title>
          <AsSyncStatusSection inspectionSlug={inspectionSlug} />
        </div>
      </Space>
    </FormProvider>
  )
}
